/**
 * Search results filter drawer (mobile).
 *
 * Slides the filters panel in from the right on small screens, matching
 * the resource TOC drawer behavior. Desktop keeps the inline sidebar.
 * Focus management mirrors site-header mobile nav (move in + Tab trap).
 */

export function initSearchResults() {
	const root = document.querySelector('[data-search-results]');
	if (!root) {
		return;
	}

	const filters = root.querySelector('[data-search-filters]');
	const panel = root.querySelector('[data-search-filters-panel]');
	const toggle = root.querySelector('[data-search-filters-toggle]');
	if (!filters || !toggle) {
		return;
	}

	const backdrop = document.createElement('div');
	backdrop.className = 'search-results__filters-backdrop';
	backdrop.setAttribute('aria-hidden', 'true');
	document.body.appendChild(backdrop);

	const focusableSelector = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(', ');

	let previouslyFocusedElement = null;

	const getFocusableElements = (container) => {
		if (!container) {
			return [];
		}

		return Array.from(container.querySelectorAll(focusableSelector)).filter((el) => {
			if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
				return false;
			}

			const style = window.getComputedStyle(el);
			if (style.display === 'none' || style.visibility === 'hidden') {
				return false;
			}

			return true;
		});
	};

	const isOpen = () => filters.classList.contains('is-open');

	const open = () => {
		previouslyFocusedElement =
			document.activeElement instanceof HTMLElement ? document.activeElement : null;

		filters.classList.add('is-open');
		root.classList.add('is-filters-open');
		backdrop.classList.add('is-visible');
		toggle.setAttribute('aria-expanded', 'true');
		document.body.style.overflow = 'hidden';

		window.requestAnimationFrame(() => {
			const focusables = getFocusableElements(panel || filters);
			if (focusables.length > 0) {
				focusables[0].focus();
			} else {
				toggle.focus();
			}
		});
	};

	const close = () => {
		filters.classList.remove('is-open');
		root.classList.remove('is-filters-open');
		backdrop.classList.remove('is-visible');
		toggle.setAttribute('aria-expanded', 'false');
		document.body.style.overflow = '';

		const focusTarget =
			previouslyFocusedElement && document.contains(previouslyFocusedElement)
				? previouslyFocusedElement
				: toggle;

		window.requestAnimationFrame(() => {
			focusTarget.focus();
		});
		previouslyFocusedElement = null;
	};

	toggle.addEventListener('click', () => {
		if (isOpen()) {
			close();
		} else {
			open();
		}
	});

	backdrop.addEventListener('click', close);

	document.addEventListener('keydown', (event) => {
		if (!isOpen()) {
			return;
		}

		if (event.key === 'Escape') {
			close();
			return;
		}

		if (event.key !== 'Tab') {
			return;
		}

		const focusables = getFocusableElements(filters);
		if (focusables.length === 0) {
			event.preventDefault();
			return;
		}

		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const current = document.activeElement;

		if (event.shiftKey) {
			if (current === first || !filters.contains(current)) {
				event.preventDefault();
				last.focus();
			}
		} else if (current === last || !filters.contains(current)) {
			event.preventDefault();
			first.focus();
		}
	});

	const desktopMq = window.matchMedia('(min-width: 1024px)');
	desktopMq.addEventListener('change', (event) => {
		if (event.matches && isOpen()) {
			close();
		}
	});
}
